import React, { useState, useMemo } from 'react';
import styles from './Card.module.scss';
import { useDrag } from 'react-dnd';
import { HeaUnit, ModeTypeEnum } from '../../../../types';
import Tooltip from '@src/components/tooltip/Tooltip';
import { getMediaAbsolutePath } from '../../../../services/File';
import IconRemovUnit from 'src/assets/icons/remove_unit.svg?react';
import useDragScrolling from "./useDragScrolling";
import { DraggableProvided, DraggableStateSnapshot } from 'react-beautiful-dnd';
import { LessonToConfirmFragment } from '@src/api/graphql-codegen';

type Props = {
  type: string
  source?: number
  target?: string
  unit: HeaUnit
  isDragable: boolean
  handleLeftArrowAction?: (unit: HeaUnit) => void
  handleRightArrowAction?: (unit: HeaUnit) => void
  handleOpenUnit?: (unit: HeaUnit) => void
  draggableProvided?: DraggableProvided;
  snapshot?: DraggableStateSnapshot;
  studyMode?: string
  confirmLessons?: LessonToConfirmFragment[]
}

function Card(props: Props) {
  const [isHovered, setIsHovered] = useState(false);
  const { type, unit, source, isDragable, handleOpenUnit, handleRightArrowAction, draggableProvided, studyMode, confirmLessons } = props
  const { addEventListenerForWindow, removeEventListenerForWindow } = useDragScrolling();

  const [{ opacity, isDragging }, drag] = useDrag(
    () => ({
      type,
      item: () => {
        // like begin drag
        addEventListenerForWindow();
        return { unit }
      },
      canDrag: isDragable,
      end: () => {
        // like end drag
        removeEventListenerForWindow();
      },
      collect: (monitor) => ({
        isDragging: !!monitor.isDragging(),
        opacity: !!monitor.isDragging() ? 0.4 : 1,
      }),
    }),
    [unit, type, isDragable],
  )

  const actionContent = useMemo(() => {
    switch (source) {
      case 0: {
        return null
      }
      default: {
        return <>
          <Tooltip position={'right'} tooltip={'Remove Unit'}>
            <IconRemovUnit onClick={() => handleRightArrowAction && handleRightArrowAction(unit)} className={styles.arrowLeftIcon}/>
          </Tooltip>
        </>
      }
    }
  }, [unit, source, handleRightArrowAction]);

  const completedBadge = useMemo(() => {
    if (studyMode === ModeTypeEnum.Study) {
      return confirmLessons?.filter(item => item.unitId === unit.id).length ?? 0
    }
    return 0;
  },[confirmLessons, studyMode, unit.id])

  const cardStyle = useMemo(() => {
    if (studyMode === ModeTypeEnum.Study) {
      return isHovered ? styles.roundCardHovered : styles.roundCard
    }
    return isHovered ? styles.cardHovered : styles.card
  },[isHovered, studyMode])

  return (
    <div ref={drag} className={cardStyle} onClick={() => handleOpenUnit?.(unit)} onMouseOver={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}
      style={{ backgroundImage: `url(${unit.coverImage ? getMediaAbsolutePath(unit.coverImage) : ''})`, opacity, cursor: isDragging ? 'grabbing' : isDragable ? 'grab' : 'pointer' }} {...draggableProvided?.dragHandleProps}>
      <div className={styles.iconView}>
        {(completedBadge > 0) && <div className={styles.greenBadge}>{completedBadge}</div>}
        <div className={styles.iconWrapper}>
          {actionContent}
        </div>
      </div>
    </div>
  );
}

export default Card
